import { createTheme } from '@mui/material/styles'

import { brand, FONT_FAMILY, functional, surfaces, textColors } from './tokens'

/**
 * Tema único com os dois esquemas de cor.
 *
 * `motion.reducedMotion: 'system'` faz as transições do próprio MUI respeitarem
 * `prefers-reduced-motion` — o Motion cuida do resto pelo `MotionConfig`.
 */
export const theme = createTheme({
  cssVariables: { colorSchemeSelector: 'data-mui-color-scheme' },
  defaultColorScheme: 'dark',
  motion: { reducedMotion: 'system' },
  colorSchemes: {
    dark: {
      palette: {
        mode: 'dark',
        primary: { main: brand.accent, contrastText: '#161616' },
        background: { default: surfaces.dark.default, paper: surfaces.dark.paper },
        text: { primary: textColors.dark.primary, secondary: textColors.dark.secondary },
        divider: surfaces.dark.border,
        ...functionalPalette(),
      },
    },
    light: {
      palette: {
        mode: 'light',
        primary: { main: brand.accent, contrastText: '#161616' },
        background: { default: surfaces.light.default, paper: surfaces.light.paper },
        text: { primary: textColors.light.primary, secondary: textColors.light.secondary },
        divider: surfaces.light.border,
        ...functionalPalette(),
      },
    },
  },
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: FONT_FAMILY,
    h5: { fontWeight: 600, fontSize: '1.5rem' },
    h6: { fontWeight: 500, fontSize: '1.25rem' },
    body1: { fontSize: '1rem', fontWeight: 400 },
    button: { textTransform: 'none', fontWeight: 500 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Números de KPI e de tabela alinham em colunas.
        '.tabular': { fontVariantNumeric: 'tabular-nums' },
        ':focus-visible': { outlineOffset: 2 },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme: current }) => ({
          backgroundImage: 'none',
          border: `1px solid ${current.palette.divider}`,
          ...(current.palette.mode === 'dark' ? { backdropFilter: 'blur(12px)' } : {}),
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        // No tema claro o verde vira só o filete: texto verde sobre branco dá 1,2:1.
        text: ({ theme: current }) => ({
          color: current.palette.text.primary,
          textDecoration: 'underline',
          textDecorationColor: brand.accent,
          textDecorationThickness: 2,
          textUnderlineOffset: 4,
        }),
      },
    },
    // Regra de marca: no tema claro o verde não é cor de texto (1,2:1 sobre
    // branco). Estes são os pontos em que o MUI usa `primary.main` como texto.
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme: current }) => ({
          '&.Mui-focused': {
            ...current.applyStyles('light', { color: current.palette.text.primary }),
          },
        }),
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: ({ theme: current }) => ({
          '&.MuiTypography-colorPrimary': {
            ...current.applyStyles('light', { color: current.palette.text.primary }),
          },
        }),
      },
    },
    // O verde como traço fino sobre fundo claro também não se enxerga; o
    // indicador de carregamento acompanha a cor do texto.
    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: ({ theme: current }) => ({
          ...current.applyStyles('light', { color: current.palette.text.primary }),
        }),
      },
    },
    MuiLink: {
      styleOverrides: {
        root: ({ theme: current }) => ({
          color: current.palette.text.primary,
          textDecorationColor: brand.accent,
          textDecorationThickness: 2,
          textUnderlineOffset: 3,
        }),
      },
    },
  },
})

function functionalPalette() {
  return {
    success: { main: functional.success, contrastText: '#FFFFFF' },
    warning: { main: functional.warning, contrastText: '#161616' },
    error: { main: functional.error, contrastText: '#FFFFFF' },
    info: { main: functional.info, contrastText: '#FFFFFF' },
  }
}
